// Configuração da Reunião Geral (admin).
//
// O que se edita aqui não é enfeite: dois blocos da pauta DEPENDEM deste
// cadastro e, sem ele, se anunciam vazios na tela que o escritório lê —
// "Nenhuma frente cadastrada" e "nenhuma fonte de notícia".
//
// 🚨 O `porquê` da frente é texto de GENTE. O robô nunca escreve essa frase:
// é estratégia da empresa numa página que 19 pessoas leem, e inventá-la seria
// pôr palavra na boca do dono. Quando está em branco, a pauta declara que
// falta — em vez de encher linguiça.
import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Button, Divider, Input, InputNumber, Modal, Space, Switch, Table, Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { api, erroMsg } from './utils';
import { Frente, PautaConfig } from './types';

const { Text, Paragraph } = Typography;

const DIAS = ['', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];

export default function ModalConfig({ aberto, onFechar }: {
  aberto: boolean;
  onFechar: () => void;
}) {
  const { message } = AntApp.useApp();
  const [cfg, setCfg] = useState<PautaConfig | null>(null);
  const [frentes, setFrentes] = useState<Frente[]>([]);
  const [destinatarios, setDestinatarios] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const d = await api('op_pauta_config_get', {});
      const c: PautaConfig = d?.config || d;
      setCfg(c);
      setFrentes(Array.isArray(c?.frentes) ? c.frentes : []);
      setDestinatarios((Array.isArray(c?.destinatarios_aviso) ? c.destinatarios_aviso : []).join(', '));
    } catch (e: any) {
      message.error('Falha ao carregar a configuração: ' + erroMsg(e));
    } finally { setCarregando(false); }
  }, [message]);

  useEffect(() => { if (aberto) carregar(); }, [aberto, carregar]);

  async function salvar() {
    if (!cfg) return;
    setSalvando(true);
    try {
      await api('op_pauta_config_set', {
        ativo: cfg.ativo,
        dia_semana: cfg.dia_semana,
        hora: cfg.hora,
        destinatarios_aviso: destinatarios.split(',').map((e) => e.trim()).filter(Boolean),
        frentes: frentes.filter((f) => (f.nome || '').trim()),
      });
      message.success('Configuração salva.');
      onFechar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + erroMsg(e));
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open={aberto}
      onCancel={onFechar}
      title="Configuração da Reunião Geral"
      width="94vw"
      style={{ maxWidth: 900, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' } }}
      okText="Salvar"
      confirmLoading={salvando}
      onOk={salvar}
      okButtonProps={{ disabled: !cfg || carregando }}
    >
      {cfg && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="center">
            <Switch checked={!!cfg.ativo} onChange={(v) => setCfg({ ...cfg, ativo: v })} />
            <Text>Montar a pauta automaticamente toda segunda de manhã</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Desligado, o robô não monta nada e a pauta passa a ser criada na mão.
          </Text>

          <Space wrap align="end" size="large">
            <Space direction="vertical" size={2}>
              <Text strong>Dia da reunião</Text>
              <InputNumber min={1} max={7} value={cfg.dia_semana}
                onChange={(v) => setCfg({ ...cfg, dia_semana: Number(v) || 5 })}
                addonAfter={DIAS[cfg.dia_semana] || ''} style={{ width: 190 }} />
            </Space>
            <Space direction="vertical" size={2}>
              <Text strong>Hora</Text>
              <Input value={cfg.hora} placeholder="14:00" style={{ width: 120 }}
                onChange={(e) => setCfg({ ...cfg, hora: e.target.value })} />
            </Space>
          </Space>

          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>Quem recebe o rascunho na segunda</Text>
            <Input value={destinatarios} placeholder="lucas@hydrodataeng.com.br, ..."
              onChange={(e) => setDestinatarios(e.target.value)} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Separados por vírgula. O escritório só é avisado quando a pauta é publicada.
            </Text>
          </Space>

          <Divider style={{ margin: '4px 0' }} />

          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong>Frentes da empresa — o bloco “Para onde vamos”</Text>
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>
              Uma frente por semana, em rodízio. O <b>por quê</b> é seu — o robô nunca
              escreve essa frase, e a pauta avisa quando ela está em branco. É essa frase
              que faz o bloco valer os 10 minutos.
            </Paragraph>
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              dataSource={frentes}
              pagination={false}
              locale={{ emptyText: 'Nenhuma frente cadastrada — sem isso o bloco não acontece.' }}
              columns={[
                {
                  title: 'Frente', dataIndex: 'nome', width: '32%',
                  render: (v: string, _r: Frente, i: number) => (
                    <Input value={v} placeholder="Topografia e batimetria"
                      onChange={(e) => setFrentes(frentes.map((f, j) =>
                        (j === i ? { ...f, nome: e.target.value } : f)))} />
                  ),
                },
                {
                  title: 'Por que é uma aposta nossa', dataIndex: 'porque',
                  render: (v: string, _r: Frente, i: number) => (
                    <Input.TextArea value={v} autoSize={{ minRows: 2, maxRows: 5 }}
                      placeholder="Em duas ou três frases, para quem não é da área."
                      onChange={(e) => setFrentes(frentes.map((f, j) =>
                        (j === i ? { ...f, porque: e.target.value } : f)))} />
                  ),
                },
                {
                  title: '', key: 'acao', width: 48,
                  render: (_: any, __: Frente, i: number) => (
                    <Button type="text" danger icon={<DeleteOutlined />}
                      onClick={() => setFrentes(frentes.filter((_f, j) => j !== i))} />
                  ),
                },
              ]}
            />
            <Button size="small" icon={<PlusOutlined />} style={{ marginTop: 8 }}
              disabled={frentes.length >= 12}
              onClick={() => setFrentes([...frentes, { nome: '', porque: '' }])}>
              Adicionar frente
            </Button>
          </Space>
        </Space>
      )}
    </Modal>
  );
}
